import { CollectionRef } from "@/types/collection"
import { EntityRef } from "@/types/entity"
import { HotelBookingRef } from "@/types/hotels/hotels"
import {
  AgentSessionEvent,
  AgentSessionEventData,
  CollaboratorEventData,
  EmailVerifyEvent,
  EmailVerifyEventData,
  EvaluationRunEvent,
  EvaluationRunEventData,
  FlightBookingEvent,
  FlightBookingEventData,
  FolioDocumentUserEvent,
  FolioDocumentUserEventData,
  HotelBookingEvent,
  HotelBookingEventData,
  PlaceEvent,
  PlaceEventData,
  SavedCollectionEvent,
  SavedCollectionEventData,
  SubscribableObjectEvent,
  SubscribableObjectEventCallback,
  TripEvent,
  TripEventData,
  VoiceChatEvent,
  VoiceChatEventData,
} from "@/types/socket"
import { isUserRef, UserRef } from "@/types/user"

import { errorMessage } from "@/lib/exception-utils"
import { assert } from "@/lib/utils/assertions"
import {
  traceVoiceObjectSubscriptionAuthorized,
  traceVoiceObjectSubscriptionEmit,
  traceVoiceObjectSubscriptionFailed,
  traceVoiceObjectSubscriptionQueued,
  traceVoiceObjectSubscriptionRequested,
  traceVoiceObjectSubscriptionUnsubscribed,
} from "@/lib/voice-trace/voice-trace"
import { SocketManager } from "./socket-manager"

export class ObjectSubscriptionManager {
  static _instance: ObjectSubscriptionManager | undefined
  private subscribers: Array<Subscription<SubscribableObjectEvent>> = []
  private socketConnectedCallbacks: Array<() => void> = []
  private collaboratorCallback:
    | undefined
    | ((userRef: UserRef, data: CollaboratorEventData) => void)

  private constructor() {
    // singleton
  }

  static get instance() {
    if (!this._instance) {
      this._instance = new ObjectSubscriptionManager()
    }
    return this._instance
  }

  // Convenience for subscribing to a Place object.
  async subscribeToPlace(
    entityRef: EntityRef,
    callback: (data: PlaceEventData) => void,
    behavior: "until-unsubscribed" | "once" = "until-unsubscribed"
  ) {
    await this.subscribe<PlaceEvent>("place", entityRef, callback, behavior)
  }

  // Convenience for subscribing to a HotelBooking object.
  async subscribeToHotelBooking(
    ref: HotelBookingRef,
    callback: (data: HotelBookingEventData) => void,
    behavior: "until-unsubscribed" | "once" = "until-unsubscribed"
  ) {
    await this.subscribe<HotelBookingEvent>(
      "hotel_booking",
      ref,
      callback,
      behavior
    )
  }

  // Convenience for subscribing to a FlightBooking object.
  async subscribeToFlightBooking(
    id: number,
    callback: (data: FlightBookingEventData) => void,
    behavior: "until-unsubscribed" | "once" = "until-unsubscribed"
  ) {
    await this.subscribe<FlightBookingEvent>(
      "flight_booking",
      id,
      callback,
      behavior
    )
  }

  // Convenience for subscribing to a trip AND assigning a callback for
  // collaborator events that fire as a side effect of subscribing to the trip.
  async subscribeToTrip(
    id: number,
    callback: (data: TripEventData) => void,
    collaboratorCallback: (
      userRef: UserRef,
      data: CollaboratorEventData
    ) => void
  ) {
    await this.subscribe<TripEvent>("trip", id, callback)
    this.collaboratorCallback = collaboratorCallback
  }

  // Convenience for subscribing to a saved collection. Only its owner and
  // accepted collaborators are authorized.
  async subscribeToCollection(
    ref: CollectionRef,
    callback: (data: SavedCollectionEventData) => void
  ) {
    await this.subscribe<SavedCollectionEvent>(
      "saved_collection",
      ref,
      callback
    )
  }

  // Convenience for subscribing to a verify email object.
  async subscribeToEmailVerify(
    userRef: UserRef,
    callback: (data: EmailVerifyEventData) => void,
    behavior: "until-unsubscribed" | "once" = "until-unsubscribed"
  ) {
    await this.subscribe<EmailVerifyEvent>(
      "email_verify",
      userRef,
      callback,
      behavior
    )
  }

  // Convenience for subscribing to a folio document.
  async subscribeToFolioDocumentUser(
    userRef: UserRef,
    callback: (data: FolioDocumentUserEventData) => void
  ) {
    await this.subscribe<FolioDocumentUserEvent>(
      "folio_document_user",
      userRef,
      callback
    )
  }

  // Convenience for subscribing to agent session updates.
  async subscribeToAgentSession(
    sessionId: number,
    callback: (data: AgentSessionEventData) => void,
    behavior: "until-unsubscribed" | "once" = "until-unsubscribed"
  ) {
    await this.subscribe<AgentSessionEvent>(
      "agent_session",
      sessionId,
      callback,
      behavior
    )
  }

  async subscribeToVoiceChat(
    chatId: number,
    callback: (data: VoiceChatEventData) => void,
    behavior: "until-unsubscribed" | "once" = "until-unsubscribed"
  ) {
    await this.subscribe<VoiceChatEvent>(
      "voice_chat",
      chatId,
      callback,
      behavior
    )
  }

  addSocketConnectedCallback(callback: () => void) {
    this.socketConnectedCallbacks.push(callback)
    return () => {
      this.socketConnectedCallbacks = this.socketConnectedCallbacks.filter(
        (registeredCallback) => registeredCallback !== callback
      )
    }
  }

  // Convenience for subscribing to evaluation run updates.
  async subscribeToEvaluationRun(
    runId: number,
    callback: (data: EvaluationRunEventData) => void,
    behavior: "until-unsubscribed" | "once" = "until-unsubscribed"
  ) {
    await this.subscribe<EvaluationRunEvent>(
      "agent_evaluation_run",
      runId,
      callback,
      behavior
    )
  }

  async subscribe<T extends SubscribableObjectEvent>(
    objectType: T["object_type"],
    objectId: string | number,
    callback: SubscribableObjectEventCallback<T>,
    behavior: "until-unsubscribed" | "once" = "until-unsubscribed"
  ) {
    assert(objectId, `invalid objectId: ${objectId}`)
    assert(
      !this.subscribers.map((s) => s.callback).includes(callback),
      "already subscribed"
    )
    const subscription = {
      objectType,
      objectId,
      callback,
      once: behavior === "once",
    }
    traceVoiceObjectSubscriptionRequested({
      objectType,
      objectId,
      once: behavior === "once",
    })
    this.subscribers.push(subscription)

    // A subscription is socket demand.
    if (!SocketManager.hasInstance) {
      SocketManager.start()
    }

    // An emitWithAck on a disconnected socket can queue forever, so gate on
    // the actual connection. A queued subscription is established by
    // onSocketConnected(), which runs on every (re)connect; check() nudges
    // a socket that has given up.
    if (!SocketManager.instance.connected) {
      traceVoiceObjectSubscriptionQueued({ objectType, objectId })
      void SocketManager.check()
      return
    }

    await this.establishServerSubscription(subscription)
  }

  unsubscribe<T extends SubscribableObjectEvent>(
    callback: SubscribableObjectEventCallback<T>
  ) {
    const index = this.subscribers.findIndex((s) => s.callback === callback)
    if (index !== -1) {
      const removedSubscription = this.subscribers.splice(index, 1)[0]

      const { objectType, objectId } = removedSubscription
      if (
        !this.hasSubscribersFor(objectType, objectId) &&
        removedSubscription.id &&
        SocketManager.hasInstance
      ) {
        // Tell the server we don't need this subscription anymore
        SocketManager.instance.unsubscribe(removedSubscription.id)
        traceVoiceObjectSubscriptionUnsubscribed({
          objectType: removedSubscription.objectType,
          objectId: removedSubscription.objectId,
          subscriptionId: removedSubscription.id,
        })

        if (removedSubscription.objectType === "trip") {
          delete this.collaboratorCallback
        }
      }
    }
  }

  onSocketConnected() {
    // Tell the server about each one of our subscriptions so it can
    // route messages to us. These are subs that accumulated before
    // the socket was ready. Since we're iterating asynchronously,
    // let's work from a copy of the subscriber list and make sure
    // each subscriber is still in the live list before issuing the
    // server call.
    //
    // Note that on some clients (mobile Safari), the socket may drop
    // when the browser is inactive but reconnect when it comes back
    // into focus. We'll land her on reconnection and need to reestablish
    // our subscriptions.
    // TODO: some subscribers (e.g. trip subscription) may want to know
    // that this happened so we can refetch any missed data.
    const subscribers = this.subscribers.slice()
    subscribers.forEach(async (s) => {
      if (this.subscribers.includes(s)) {
        await this.establishServerSubscription(s)
      }
    })
    this.socketConnectedCallbacks.forEach((callback) => callback())
  }

  // The server room is shared by every subscription to an object, so it may
  // only be released once nothing is left in it.
  private hasSubscribersFor(
    objectType: SubscribableObjectEvent["object_type"],
    objectId: string | number
  ) {
    return this.subscribers.some(
      (s) => s.objectType === objectType && s.objectId === objectId
    )
  }

  private async establishServerSubscription(
    subscription: Subscription<SubscribableObjectEvent>
  ) {
    try {
      // Note: sending multiple server calls to subscribe to an object
      // will result in just one subscription; all but the first is a
      // no-op. Since we may have multiple client-side subscriptions for
      // the same object, we issue a subscribe call for each one but
      // wait to issue the unsubscribe until the last one is removed.

      const id = await SocketManager.instance.subscribe(
        subscription.objectType,
        subscription.objectId
      )
      if (!this.subscribers.includes(subscription)) {
        // Removed while the ack was in flight. A sibling may still be sitting
        // in the room this ack just confirmed.
        if (
          !this.hasSubscribersFor(
            subscription.objectType,
            subscription.objectId
          )
        ) {
          SocketManager.instance.unsubscribe(id)
        }
        return
      }

      subscription.id = id
      traceVoiceObjectSubscriptionAuthorized({
        objectType: subscription.objectType,
        objectId: subscription.objectId,
        subscriptionId: id,
      })
    } catch (e) {
      traceVoiceObjectSubscriptionFailed({
        objectType: subscription.objectType,
        objectId: subscription.objectId,
        message: errorMessage(e),
      })
      console.error(
        errorMessage(e),
        subscription.objectType,
        subscription.objectId,
        e
      )
    }
  }

  emit(payload: SubscribableObjectEvent) {
    const { object_type, object_id, data } = payload
    const matchers = this.subscribers.filter(
      (s) => s.objectType === object_type && s.objectId === object_id
    )
    traceVoiceObjectSubscriptionEmit({
      objectType: object_type,
      objectId: object_id,
      matcherCount: matchers.length,
      oneShotMatcherCount: matchers.filter((s) => s.once).length,
    })
    matchers.forEach((subscription) => subscription.callback(data))

    // Also notify the special collaborator callback, if any. These
    // events arrive as a side effect of subscribing to a trip, so
    // they won't match the normal subscriptions.
    if (payload.object_type === "collaborator") {
      assert(isUserRef(object_id), "object_id is not a user ref")
      this.collaboratorCallback?.(object_id, payload.data)
    }

    // unsubscribe() rather than a local removal: the server room membership
    // must be released too.
    matchers.filter((s) => s.once).forEach((s) => this.unsubscribe(s.callback))
  }
}

interface Subscription<T extends SubscribableObjectEvent> {
  objectType: T["object_type"]
  objectId: string | number
  callback: (payload: T["data"]) => void
  once: boolean
  id?: string
}
